/**
 * Stable identity for the trip-permissions section. The granular layout swaps
 * this one section out (TS-8933), and matching on the display title would break
 * silently the moment the title is reworded or localised — the filter would
 * no-op and the modal would render BOTH the legacy and granular trip toggles.
 */
export const TRIP_SECTION_ID = 'trips';

export const PERMISSION_SECTIONS = [
  {
    id: TRIP_SECTION_ID,
    title: 'Work with Trips',
    items: [
      {
        label: 'Write Trips',
        group: 'trip_requests',
        permission: 'write',
      },
      {
        label: 'Read Trips',
        group: 'trip_requests',
        permission: 'read',
      },
      {
        label: 'Assign Trips',
        group: 'trip_requests',
        permission: 'assign',
      },
      {
        label: 'Cancel Trips',
        group: 'trip_requests',
        permission: 'cancel',
      },
    ],
  },
  {
    title: 'Work with Agreements',
    items: [
      {
        label: 'Write Agreements',
        group: 'admin',
        permission: 'write_agreements',
      },
      {
        label: 'Read Agreement',
        group: 'admin',
        permission: 'read_agreements',
      },
    ],
  },
  {
    title: 'Work with Members',
    items: [
      {
        label: 'Write Members',
        group: 'patients',
        permission: 'write',
      },
      {
        label: 'Read Members',
        group: 'patients',
        permission: 'read',
      },
    ],
  },
  {
    title: 'Work with Users',
    items: [
      {
        label: 'Invite User',
        group: 'admin',
        permission: 'invite_users',
      },
      { label: 'Read User', group: 'users', permission: 'read' },
      {
        label: 'Write Roles',
        group: 'admin',
        permission: 'write_roles',
      },
      {
        label: 'Read Roles',
        group: 'admin',
        permission: 'read_roles',
      },
      {
        label: 'Remove Users',
        group: 'admin',
        permission: 'remove_users',
      },
    ],
  },
  {
    title: 'System Settings',
    items: [
      {
        label: 'Update Organization Account',
        group: 'admin',
        permission: 'update_organization_account',
      },
      {
        label: 'Read Organization Account',
        group: 'organization_account',
        permission: 'read',
      },
      {
        label: 'Write Configs',
        group: 'admin',
        permission: 'write_configs',
      },
      { label: 'Read Configs', group: 'configs', permission: 'read' },
      {
        label: 'Override Warnings',
        group: 'trip_requests',
        permission: 'override_warnings',
      },
      {
        label: 'Override Limits',
        group: 'trip_requests',
        permission: 'override_limits',
      },
      {
        label: 'Enroll Support',
        group: 'admin',
        permission: 'enroll_support',
      },
    ],
  },
  {
    title: 'External Credentials',
    items: [
      {
        label: 'Write External Credentials',
        group: 'admin',
        permission: 'write_external_credentials',
      },
      {
        label: 'Read External Credentials',
        group: 'admin',
        permission: 'read_external_credentials',
      },
    ],
  },
];

/**
 * TS-8933 — interim granular trip permissions.
 *
 * Replaces the legacy four trip toggles (Write / Read / Assign / Cancel) with
 * the granular set, and carves escalation actions into their own section.
 * `write` is deliberately absent: the epic removes the monolithic toggle from
 * the modal, though the key may remain in the data model during the interim.
 *
 * Order matches the design (2-up grid, reading left-to-right).
 * Gated behind USE_GRANULAR_TRIP_PERMISSIONS_FLAG.
 */
export const GRANULAR_TRIP_PERMISSION_SECTIONS = [
  {
    id: TRIP_SECTION_ID,
    title: 'Work with Trips',
    items: [
      { label: 'Create Trips', group: 'trip_requests', permission: 'create' },
      { label: 'Read Trips', group: 'trip_requests', permission: 'read' },
      {
        label: 'Escalate Trips',
        group: 'trip_requests',
        permission: 'escalate_for_approval',
      },
      { label: 'Deny Trips', group: 'trip_requests', permission: 'deny' },
      { label: 'Assign Trips', group: 'trip_requests', permission: 'assign' },
      {
        label: 'Bulk Assign Trips',
        group: 'trip_requests',
        permission: 'bulk_assign',
      },
      { label: 'Update Trips', group: 'trip_requests', permission: 'update' },
      {
        label: 'Bulk Update Trips',
        group: 'trip_requests',
        permission: 'bulk_update',
      },
      { label: 'Cancel Trips', group: 'trip_requests', permission: 'cancel' },
      {
        label: 'Bulk Cancel Trips',
        group: 'trip_requests',
        permission: 'bulk_cancel',
      },
    ],
  },
  {
    title: 'Escalations',
    items: [
      {
        label: 'Read Escalations',
        group: 'trip_requests',
        permission: 'read_escalation_requests',
      },
      {
        label: 'Deny Escalations',
        group: 'trip_requests',
        permission: 'deny_escalation_requests',
      },
      {
        label: 'Approve Escalations',
        group: 'trip_requests',
        permission: 'approve_escalation_requests',
      },
    ],
  },
];

/**
 * The modal's non-trip sections, unchanged by TS-8933. Shared by both the
 * legacy and granular layouts so the two can never drift.
 */
export const NON_TRIP_PERMISSION_SECTIONS = PERMISSION_SECTIONS.filter(
  (section) => section.id !== TRIP_SECTION_ID,
);

/** Full section list for the granular (flag-on) layout. */
export const PERMISSION_SECTIONS_GRANULAR = [
  ...GRANULAR_TRIP_PERMISSION_SECTIONS,
  ...NON_TRIP_PERMISSION_SECTIONS,
];

/** Lookup key for a stored permission, i.e. `trip_requests` + `create`. */
export const permissionKey = (group, permission) => `${group}.${permission}`;

/**
 * TS-9863: permissions kept out of the UI. The modal shows no toggle for them
 * and the Roles table no chip, but they stay in the sections above (so their
 * labels stay defined) and in the data model. Editing a role sends its full
 * stored permission set, so a role that already holds one keeps it; it just
 * can't be granted from here.
 */
export const HIDDEN_PERMISSION_KEYS = new Set([
  permissionKey('trip_requests', 'override_warnings'),
  permissionKey('trip_requests', 'override_limits'),
  permissionKey('admin', 'read_external_credentials'),
  permissionKey('admin', 'write_external_credentials'),
  permissionKey('organization_account', 'read'),
  permissionKey('admin', 'update_organization_account'),
  permissionKey('admin', 'enroll_support'),
]);

export const isHiddenPermission = (group, permission) =>
  HIDDEN_PERMISSION_KEYS.has(permissionKey(group, permission));

/** Sections as the modal renders them: hidden items out, emptied sections dropped. */
export const visibleSections = (sections) =>
  sections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => !isHiddenPermission(item.group, item.permission),
      ),
    }))
    .filter((section) => section.items.length > 0);

/**
 * TS-9454: the single source of truth for permission display names.
 *
 * The Roles table used to prettify the raw stored keys (group and permission
 * each run through `wordCapitalize`), so one permission read "Trip Requests
 * Create" in the table but "Create Trips" in this modal. Deriving the map from
 * the sections above means a label can only ever be written once: add an item
 * to a section and it is named identically everywhere.
 *
 * Both layouts contribute, because a role can hold keys from either while the
 * granular flag is rolling out. The keys the two share (read, cancel, assign)
 * carry identical labels, so the merge order does not matter.
 */
export const PERMISSION_LABELS = Object.fromEntries(
  [...PERMISSION_SECTIONS, ...GRANULAR_TRIP_PERMISSION_SECTIONS].flatMap(
    (section) =>
      section.items.map((item) => [
        permissionKey(item.group, item.permission),
        item.label,
      ]),
  ),
);
